// Fondos cinéticos en canvas 2D, portados del prototipo de Claude Design.
//
//   [data-psyque-bg="grid"]  → trama de puntos que se iluminan cerca del cursor
//   [data-psyque-bg="nodes"] → red de nodos a la deriva; el cursor tiende
//                              líneas hacia los nodos cercanos
//
// Ambos: pausan fuera del viewport y con la pestaña oculta, limitan el DPR y
// se desactivan por completo con prefers-reduced-motion (el contenido no
// depende de ellos en ningún caso).
const DPR = () => Math.min(window.devicePixelRatio || 1, 1.75);

function setupGrid(canvas) {
	const ctx = canvas.getContext("2d");
	const host = canvas.parentElement;
	const DENSITY = 34;
	const RADIUS = 90;
	const STRENGTH = 0.3;

	let width = 0;
	let height = 0;
	let points = [];
	let visible = true;
	let mouse = null;
	let raf = null;

	const build = () => {
		const rect = host.getBoundingClientRect();
		width = rect.width;
		height = rect.height;
		if (!width || !height) return;
		const dpr = DPR();
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

		const cols = DENSITY;
		const rows = Math.max(2, Math.round(DENSITY * (height / width)));
		points = [];
		for (let i = 0; i < cols; i++) {
			for (let j = 0; j < rows; j++) {
				points.push({ x: (i / (cols - 1)) * width, y: (j / (rows - 1)) * height });
			}
		}
	};

	const onMove = (event) => {
		const rect = host.getBoundingClientRect();
		mouse = { x: event.clientX - rect.left, y: event.clientY - rect.top };
	};
	const onLeave = () => {
		mouse = null;
	};

	const tick = () => {
		if (visible && !document.hidden) {
			ctx.clearRect(0, 0, width, height);
			for (const point of points) {
				let alpha = 0.12;
				if (mouse) {
					const distance = Math.hypot(point.x - mouse.x, point.y - mouse.y);
					if (distance < RADIUS) alpha += (1 - distance / RADIUS) * STRENGTH * 3;
				}
				ctx.beginPath();
				ctx.fillStyle = `rgba(29,220,255,${Math.min(1, alpha).toFixed(3)})`;
				ctx.arc(point.x, point.y, 1.4, 0, Math.PI * 2);
				ctx.fill();
			}
		}
		raf = window.requestAnimationFrame(tick);
	};

	build();
	window.addEventListener("resize", build);
	host.addEventListener("pointermove", onMove, { passive: true });
	host.addEventListener("pointerleave", onLeave, { passive: true });
	new IntersectionObserver((entries) => {
		visible = entries[0].isIntersecting;
	}, { threshold: 0 }).observe(canvas);
	raf = window.requestAnimationFrame(tick);
}

function setupNodes(canvas) {
	const ctx = canvas.getContext("2d");
	const host = canvas.parentElement;
	const CURSOR_R = 220;
	const STRUCT_R = 90;

	let nodes = [];
	let width = 0;
	let height = 0;
	let visible = true;
	let raf = null;
	const cursor = { x: -9999, y: -9999, active: false };

	const resize = () => {
		const rect = host.getBoundingClientRect();
		width = rect.width;
		height = rect.height;
		if (!width || !height) return;
		const dpr = DPR();
		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

		const count = width < 768 ? 26 : width < 1280 ? 46 : 64;
		nodes = Array.from({ length: count }, () => ({
			x: Math.random() * width,
			y: Math.random() * height,
			vx: (Math.random() - 0.5) * 0.12,
			vy: (Math.random() - 0.5) * 0.12,
			r: 0.7 + Math.random() * 0.7,
		}));
	};

	const onMove = (event) => {
		const rect = canvas.getBoundingClientRect();
		cursor.x = event.clientX - rect.left;
		cursor.y = event.clientY - rect.top;
		cursor.active = true;
	};
	const onLeave = () => {
		cursor.active = false;
	};

	const tick = () => {
		if (visible && !document.hidden) {
			ctx.clearRect(0, 0, width, height);

			for (const node of nodes) {
				node.x += node.vx;
				node.y += node.vy;
				if (node.x < 0 || node.x > width) node.vx *= -1;
				if (node.y < 0 || node.y > height) node.vy *= -1;
			}

			// Conexiones estructurales entre nodos próximos.
			for (let i = 0; i < nodes.length; i++) {
				for (let j = i + 1; j < nodes.length; j++) {
					const a = nodes[i];
					const b = nodes[j];
					const distance = Math.hypot(a.x - b.x, a.y - b.y);
					if (distance >= STRUCT_R) continue;
					ctx.beginPath();
					ctx.strokeStyle = `rgba(120,140,220,${((1 - distance / STRUCT_R) * 0.3).toFixed(3)})`;
					ctx.lineWidth = 1;
					ctx.moveTo(a.x, a.y);
					ctx.lineTo(b.x, b.y);
					ctx.stroke();
				}
			}

			// El cursor tiende líneas cian hacia los nodos cercanos.
			if (cursor.active) {
				for (const node of nodes) {
					const distance = Math.hypot(node.x - cursor.x, node.y - cursor.y);
					if (distance >= CURSOR_R) continue;
					ctx.beginPath();
					ctx.strokeStyle = `rgba(29,220,255,${((1 - distance / CURSOR_R) * 0.45).toFixed(3)})`;
					ctx.lineWidth = 1;
					ctx.moveTo(cursor.x, cursor.y);
					ctx.lineTo(node.x, node.y);
					ctx.stroke();
				}
			}

			for (const node of nodes) {
				ctx.beginPath();
				ctx.fillStyle = "rgba(170,195,255,0.55)";
				ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
				ctx.fill();
			}
		}
		raf = window.requestAnimationFrame(tick);
	};

	resize();
	window.addEventListener("resize", resize);
	host.addEventListener("pointermove", onMove, { passive: true });
	host.addEventListener("pointerleave", onLeave, { passive: true });
	new IntersectionObserver((entries) => {
		visible = entries[0].isIntersecting;
	}, { threshold: 0 }).observe(canvas);
	raf = window.requestAnimationFrame(tick);
}

export function initBackgrounds() {
	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

	const canvases = document.querySelectorAll("canvas[data-psyque-bg]");
	for (const canvas of canvases) {
		const mode = canvas.dataset.psyqueBg;
		if (mode === "grid") setupGrid(canvas);
		else if (mode === "nodes") setupNodes(canvas);
	}
}
